/**
 * AdminHistoricEventsPage ✦ iter231 — Historic Events portfolio builder.
 *
 * The central source-of-truth admin surface for every EventDrop event.
 * Phase A scope:
 *   • Dashboard tiles (counts + missing-field quick filters)
 *   • Manual event creator with the full rich data model
 *   • Auto-geocode + auto-venue-create on save
 *   • Duplicate detection prompt
 *   • Event Health Score live preview
 *   • Event list with filter chips, edit-in-place, delete
 *
 * Phase B (Bulk CSV) ships separately.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const PAGE_BG = '#0F0B09';
const CARD = 'rgba(28,18,12,0.78)';
const GOLD = '#D4AF5E';
const GOLD_DIM = 'rgba(212,170,82,0.42)';
const IVORY = '#F5E9C9';
const DIM = 'rgba(245,233,201,0.66)';
const RED = '#C7593B';
const GREEN = '#5E8B5A';

const EVENT_TYPE_LABELS = {
  wedding: 'Wedding', corporate: 'Corporate', birthday: 'Birthday',
  anniversary: 'Anniversary', christmas_party: 'Christmas Party',
  school_prom: 'School Prom', charity: 'Charity', baby_shower: 'Baby Shower',
  engagement: 'Engagement', other: 'Other',
};
const SOURCE_LABELS = {
  website: 'Website', facebook: 'Facebook', addtoevent: 'Add To Event',
  recommendation: 'Recommendation', venue: 'Venue',
  returning_customer: 'Returning Customer', other: 'Other',
};
const PRODUCT_LABELS = {
  'blossom-booth': 'Blossom Booth',
  'heritage-booth': 'Heritage Booth',
  'woodland-mirror-booth': 'Woodland Mirror Booth',
  'woodland-audio-guestbook': 'Audio Guestbook (Woodland)',
  'woodland-video-guestbook': 'Video Guestbook (Woodland)',
  'audio-guestbook': 'Audio Guestbook',
  'video-guestbook': 'Video Guestbook',
  'love-letters': 'LOVE Letters',
  'rustic-podiums': 'Rustic Podiums',
  'props-stand': 'Props Stand',
  'memory-box': 'Memory Box',
  'flower-wall': 'Flower Wall',
  other: 'Other',
};

const blankForm = () => ({
  title: '',
  event_date: '',
  event_type: 'wedding',
  estimated_guest_count: 0,
  venue_name: '',
  venue_postcode: '',
  venue_location: '',
  venue_contact: '',
  venue_website: '',
  package_slug: '',
  products_used: [],
  customer_name: '',
  booking_source: '',
  review_left: false,
  gallery_delivered: false,
  memory_box_supplied: false,
  audio_guestbook_included: false,
  review_text: '',
  review_rating: 0,
  hero_image_url: '',
  gallery_image_urls: [],
  gallery_folder: '',
  video_url: '',
  drone_footage_url: '',
  virtual_tour_url: '',
  is_public: true,
  is_featured: false,
  venue_partner: false,
  notes: '',
});

// ─── Health score (FE replica — used for live preview while typing) ──
const HEALTH_CHECKS_FE = [
  ['venue_name', 'Venue'], ['venue_postcode', 'Postcode'],
  ['hero_image_url', 'Hero Image'], ['gallery_image_urls', 'Gallery'],
  ['review_text', 'Review'], ['review_rating', 'Rating'],
  ['products_used', 'Products'], ['event_type', 'Event Type'],
  ['estimated_guest_count', 'Guest Count'], ['package_slug', 'Package'],
  ['customer_name', 'Customer'], ['booking_source', 'Booking Source'],
];
const livePreviewHealth = (form) => {
  let ok = 0;
  HEALTH_CHECKS_FE.forEach(([k]) => {
    const v = form[k];
    const ok1 = Array.isArray(v) ? v.length > 0
      : typeof v === 'number' ? v > 0
      : Boolean(v);
    if (ok1) ok += 1;
  });
  // +2 derived (coords + venue_created) are server-side only
  const pct = Math.round((ok / (HEALTH_CHECKS_FE.length + 2)) * 100);
  let label = 'Poor', color = RED;
  if (pct >= 90) { label = 'Excellent'; color = GREEN; }
  else if (pct >= 70) { label = 'Good'; color = GOLD; }
  else if (pct >= 40) { label = 'Fair'; color = '#C9802E'; }
  return { score: pct, label, color };
};

// ─── HTTP helper ────────────────────────────────────────────────────
const useAuthHeaders = () => useMemo(() => {
  const token =
    typeof window !== 'undefined' && (
      window.localStorage.getItem('token') ||
      window.localStorage.getItem('access_token')
    );
  return token ? { Authorization: `Bearer ${token}` } : {};
}, []);

// ─── Top-level page ─────────────────────────────────────────────────
const AdminHistoricEventsPage = () => {
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCsv, setShowCsv] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showPromoteBookings, setShowPromoteBookings] = useState(false);
  const [selectedSlugs, setSelectedSlugs] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/admin/historic-events/stats`, { headers: authHeaders, withCredentials: true }),
        axios.get(`${API}/admin/historic-events${filter ? `?filter=${filter}` : ''}`,
          { headers: authHeaders, withCredentials: true }),
      ]);
      setStats(s.data);
      setEvents(e.data?.events || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Please sign in as an admin to access Historic Events');
        navigate('/login');
        return;
      }
      toast.error('Could not load historic events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'Historic Events ✦ EventDrop Admin';
    load();
  }, [filter]);

  const onSaved = async () => {
    setShowForm(false);
    setEditing(null);
    await load();
  };

  return (
    <div style={{ minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto', padding: '40px 28px',
      }} data-testid="historic-events-page">
        <Link to="/admin" style={{ color: GOLD, textDecoration: 'none', fontSize: 13 }}>
          ← Admin
        </Link>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontStyle: 'italic', fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD }}>
              ✦ Portfolio Builder
            </div>
            <h1 style={{ fontSize: 36, fontWeight: 500, margin: '8px 0 6px', letterSpacing: '-0.02em' }}>
              Historic Events
            </h1>
            <p style={{ fontStyle: 'italic', color: DIM, fontSize: 14, margin: 0 }}>
              Add a past EventDrop event. The map pin, venue page, package stats and homepage counters update automatically.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Migrate the legacy "Recent Events" seeds onto the map?\n\nThis adds Smith Wedding (Tower Hill Barns), Ysgol Prom (Carden Park) and Emma\'s 21st (The Mill Barns). Safe to run multiple times — already-migrated events are skipped.')) return;
                try {
                  const r = await axios.post(`${API}/admin/historic-events/migrate-from-seeds`,
                    {}, { headers: authHeaders, withCredentials: true });
                  const d = r.data || {};
                  const m = d.migrated_count ?? 0;
                  const s = d.skipped_count ?? 0;
                  const e = d.error_count ?? 0;
                  // iter234b — when nothing actually migrated, frame the
                  // toast as confirmation, not noise. Steer the user to
                  // the right tool for bulk-importing their back-catalogue.
                  if (m === 0 && e === 0) {
                    toast.success(`✓ Legacy seeds already on the map (${s} skipped). To bulk-import more events, use ↑ CSV Import. If imported pins aren't showing, click ⌖ Re-geocode missing pins.`, { duration: 8000 });
                  } else {
                    toast.success(`✦ Migrated ${m} · skipped ${s} · ${e} errors`);
                  }
                  await load();
                } catch (err) {
                  toast.error('Migration failed: ' + (err?.response?.data?.detail || err.message));
                }
              }}
              data-testid="historic-events-migrate-seeds-btn"
              style={{
                background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`,
                fontFamily: SERIF, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '12px 22px', borderRadius: 999, cursor: 'pointer',
              }}
            >↻ Migrate Seeds</button>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("Re-geocode every event that's missing map coordinates?\n\nUse this after a CSV import if the map isn't showing your imported events. Safe to run multiple times — events already on the map are skipped.")) return;
                try {
                  const r = await axios.post(`${API}/admin/venues/backfill-events-geocode`,
                    { max_items: 500 }, { headers: authHeaders, withCredentials: true });
                  const d = r.data || {};
                  toast.success(`⌖ Checked ${d.checked ?? 0} · geocoded ${d.geocoded ?? 0} · ${d.skipped_no_postcode ?? 0} skipped (no postcode) · ${d.failed ?? 0} failed`, { duration: 8000 });
                  await load();
                } catch (err) {
                  toast.error('Re-geocode failed: ' + (err?.response?.data?.detail || err.message));
                }
              }}
              data-testid="historic-events-regeocode-btn"
              style={{
                background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`,
                fontFamily: SERIF, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '12px 22px', borderRadius: 999, cursor: 'pointer',
              }}
            >⌖ Re-geocode missing pins</button>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Tidy event title aliases + SEO tags across every event?\n\nUse this once to fix events whose titles were edited before the propagation bug was patched. Safe to run multiple times.')) return;
                try {
                  const r = await axios.post(`${API}/admin/historic-events/tidy-aliases`,
                    {}, { headers: authHeaders, withCredentials: true });
                  const d = r.data || {};
                  toast.success(`✓ Tidied ${d.checked ?? 0} events · ${d.name_updated ?? 0} names · ${d.date_updated ?? 0} dates · ${d.seo_updated ?? 0} SEO blocks · ${d.booking_slug_backfilled ?? 0} bookings linked`, { duration: 8000 });
                  await load();
                } catch (err) {
                  toast.error('Tidy failed: ' + (err?.response?.data?.detail || err.message));
                }
              }}
              data-testid="historic-events-tidy-aliases-btn"
              style={{
                background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`,
                fontFamily: SERIF, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '12px 22px', borderRadius: 999, cursor: 'pointer',
              }}
            >⌗ Tidy aliases</button>
            <button
              type="button"
              onClick={() => setShowPromoteBookings(true)}
              data-testid="historic-events-promote-bookings-btn"
              style={{
                background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`,
                fontFamily: SERIF, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '12px 22px', borderRadius: 999, cursor: 'pointer',
              }}
            >↗ Promote Bookings</button>
            <button
              type="button"
              onClick={() => setShowCsv(true)}
              data-testid="historic-events-csv-btn"
              style={{
                background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`,
                fontFamily: SERIF, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '12px 22px', borderRadius: 999, cursor: 'pointer',
              }}
            >↑ CSV Import</button>
            <button
              type="button"
              onClick={() => setShowDuplicates(true)}
              data-testid="historic-events-duplicates-btn"
              style={{
                background: 'transparent', color: DIM, border: `1px solid ${GOLD_DIM}`,
                fontFamily: SERIF, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '12px 22px', borderRadius: 999, cursor: 'pointer',
              }}
            >⌗ Duplicates</button>
            <button
              type="button"
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="historic-events-add-btn"
              style={{
                background: GOLD, color: '#1A120A', border: 'none',
                fontFamily: SERIF, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '12px 24px', borderRadius: 999, cursor: 'pointer', fontWeight: 600,
              }}
            >+ Add Historic Event</button>
          </div>
        </div>

        {/* Dashboard tiles */}
        {stats && (
          <div data-testid="historic-events-dashboard" style={{
            marginTop: 28, display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          }}>
            <StatTile label="Events Imported" value={stats.events_imported} testid="stat-events-imported" />
            <StatTile label="Venues Created" value={stats.venues_created} testid="stat-venues-created" />
            <StatTile label="Counties Covered" value={stats.counties_covered} testid="stat-counties-covered" />
            <StatTile label="Galleries Uploaded" value={stats.galleries_uploaded} testid="stat-galleries-uploaded" />
            <StatTile label="Reviews Imported" value={stats.reviews_imported} testid="stat-reviews-imported" />
          </div>
        )}

        {/* Quick filters */}
        {stats && (
          <div style={{ marginTop: 22, display: 'flex', gap: 8, flexWrap: 'wrap' }} data-testid="historic-events-filters">
            <FilterPill label="All" active={!filter} onClick={() => setFilter(null)} count={stats.events_imported} testid="filter-all" />
            <FilterPill label="Missing Postcode" count={stats.missing.postcode} onClick={() => setFilter('missing_postcode')} active={filter === 'missing_postcode'} testid="filter-missing_postcode" />
            <FilterPill label="Missing Photos" count={stats.missing.photos} onClick={() => setFilter('missing_photos')} active={filter === 'missing_photos'} testid="filter-missing_photos" />
            <FilterPill label="Missing Review" count={stats.missing.review} onClick={() => setFilter('missing_review')} active={filter === 'missing_review'} testid="filter-missing_review" />
            <FilterPill label="Missing Hero" count={stats.missing.hero_image} onClick={() => setFilter('missing_hero')} active={filter === 'missing_hero'} testid="filter-missing_hero" />
            <FilterPill label="Missing Package" count={stats.missing.package} onClick={() => setFilter('missing_package')} active={filter === 'missing_package'} testid="filter-missing_package" />
            <FilterPill label="Missing Products" count={stats.missing.products} onClick={() => setFilter('missing_products')} active={filter === 'missing_products'} testid="filter-missing_products" />
            <FilterPill label="Missing Event Type" count={stats.missing.event_type} onClick={() => setFilter('missing_event_type')} active={filter === 'missing_event_type'} testid="filter-missing_event_type" />
            <FilterPill label="Featured" onClick={() => setFilter('featured')} active={filter === 'featured'} testid="filter-featured" />
          </div>
        )}

        {/* Event list */}
        <div style={{ marginTop: 26, display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="historic-events-list">
          {loading && <p style={{ color: DIM, fontStyle: 'italic' }}>Loading ✦</p>}
          {!loading && events.length === 0 && (
            <div style={{
              padding: 30, textAlign: 'center', borderRadius: 12,
              background: CARD, border: `1px dashed ${GOLD_DIM}`,
            }} data-testid="historic-events-empty">
              <p style={{ color: DIM, fontStyle: 'italic', margin: 0 }}>
                No events here yet. Click <strong style={{ color: GOLD }}>+ Add Historic Event</strong> to drop your first pin on the map.
              </p>
            </div>
          )}
          {events.map(ev => (
            <EventRow
              key={ev.slug}
              event={ev}
              selected={selectedSlugs.has(ev.slug)}
              onToggle={() => setSelectedSlugs((s) => {
                const next = new Set(s);
                if (next.has(ev.slug)) next.delete(ev.slug); else next.add(ev.slug);
                return next;
              })}
              onEdit={() => { setEditing(ev); setShowForm(true); }}
              onDelete={async () => {
                if (!window.confirm(`Delete "${ev.title}"? This removes the map pin and stats.`)) return;
                try {
                  await axios.delete(`${API}/admin/historic-events/${ev.slug}`,
                    { headers: authHeaders, withCredentials: true });
                  toast.success('Event deleted');
                  await load();
                } catch { toast.error('Delete failed'); }
              }}
            />
          ))}
        </div>
      </div>

      {selectedSlugs.size > 0 && (
        <BulkToolbar
          count={selectedSlugs.size}
          slugs={Array.from(selectedSlugs)}
          authHeaders={authHeaders}
          onCleared={() => setSelectedSlugs(new Set())}
          onAfter={async () => { setSelectedSlugs(new Set()); await load(); }}
        />
      )}

      {showForm && (
        <EventFormModal
          initial={editing}
          authHeaders={authHeaders}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}

      {showCsv && (
        <CsvWizardModal
          authHeaders={authHeaders}
          onClose={() => setShowCsv(false)}
          onImported={async () => { setShowCsv(false); await load(); }}
        />
      )}

      {showDuplicates && (
        <DuplicatesModal
          authHeaders={authHeaders}
          onClose={() => setShowDuplicates(false)}
          onChanged={async () => { await load(); }}
        />
      )}

      {showPromoteBookings && (
        <PromoteBookingsModal
          authHeaders={authHeaders}
          onClose={() => setShowPromoteBookings(false)}
          onPromoted={async () => { await load(); }}
        />
      )}
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────
const StatTile = ({ label, value, testid }) => (
  <div data-testid={testid} style={{
    background: CARD, border: `1px solid ${GOLD_DIM}`, borderRadius: 12,
    padding: '18px 16px', textAlign: 'center',
  }}>
    <div style={{ fontSize: 26, color: GOLD, fontWeight: 500, lineHeight: 1.1 }}>
      {(value ?? 0).toLocaleString?.() ?? value}
    </div>
    <div style={{ fontSize: 10.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: DIM, fontStyle: 'italic', marginTop: 6 }}>
      {label}
    </div>
  </div>
);

const FilterPill = ({ label, count, onClick, active, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    style={{
      background: active ? GOLD : 'transparent',
      color: active ? '#1A120A' : DIM,
      border: `1px solid ${active ? GOLD : GOLD_DIM}`,
      fontFamily: SERIF, fontSize: 12, letterSpacing: '0.04em',
      padding: '7px 13px', borderRadius: 999, cursor: 'pointer',
    }}
  >
    {label}{typeof count === 'number' ? <span style={{ marginLeft: 6, opacity: 0.7 }}>· {count}</span> : null}
  </button>
);

const EventRow = ({ event, selected, onToggle, onEdit, onDelete }) => (
  <div data-testid={`event-row-${event.slug}`} style={{
    background: CARD, border: `1px solid ${selected ? GOLD : (event.is_featured ? GOLD : GOLD_DIM)}`,
    borderRadius: 12, padding: '14px 18px',
    display: 'grid', gap: 12,
    gridTemplateColumns: 'auto minmax(0, 1fr) auto', alignItems: 'center',
  }}>
    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
      onClick={(e) => { e.stopPropagation(); }}>
      <input type="checkbox" checked={!!selected}
        onChange={onToggle}
        onClick={(e) => { e.stopPropagation(); }}
        data-testid={`event-row-select-${event.slug}`}
        style={{ width: 18, height: 18, cursor: 'pointer' }} />
    </label>
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <strong style={{ fontSize: 16, color: IVORY, letterSpacing: '-0.005em' }}>{event.title}</strong>
        {event.is_featured && <span style={{ fontSize: 10, letterSpacing: '0.2em', color: GOLD, fontStyle: 'italic' }}>★ FEATURED</span>}
        {event.venue_partner && <span style={{ fontSize: 10, letterSpacing: '0.2em', color: GOLD, fontStyle: 'italic' }}>✦ PARTNER VENUE</span>}
        {!event.is_public && <span style={{ fontSize: 10, letterSpacing: '0.2em', color: DIM, fontStyle: 'italic' }}>PRIVATE</span>}
      </div>
      <div style={{ fontStyle: 'italic', color: DIM, fontSize: 12, marginTop: 4 }}>
        {[event.venue_name, event.event_date?.slice(0, 10), EVENT_TYPE_LABELS[event.event_type] || event.event_type, event.customer_name]
          .filter(Boolean).join(' · ')}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap', fontSize: 12 }}>
        <span data-testid={`event-row-health-${event.slug}`} style={{
          background: 'rgba(0,0,0,0.42)', border: `1px solid ${event.health?.color || GOLD_DIM}`,
          color: event.health?.color || GOLD, padding: '3px 10px', borderRadius: 999,
        }}>
          Health {event.health?.score ?? 0}% · {event.health?.label || ''}
        </span>
        {event.review_rating > 0 && (
          <span style={{ color: GOLD }}>{'★'.repeat(Math.round(event.review_rating))}</span>
        )}
        {event.estimated_guest_count > 0 && (
          <span style={{ color: DIM, fontStyle: 'italic' }}>~{event.estimated_guest_count} guests</span>
        )}
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8 }}>
      <button type="button" onClick={onEdit} data-testid={`event-row-edit-${event.slug}`} style={pillBtnGhost}>Edit</button>
      <button type="button" onClick={onDelete} data-testid={`event-row-delete-${event.slug}`} style={{ ...pillBtnGhost, color: RED, borderColor: RED }}>Delete</button>
    </div>
  </div>
);

const pillBtnGhost = {
  background: 'transparent', color: GOLD, border: `1px solid ${GOLD_DIM}`,
  fontFamily: SERIF, fontSize: 12, padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
};

// ─── Form Modal ─────────────────────────────────────────────────────
const EventFormModal = ({ initial, authHeaders, onClose, onSaved }) => {
  const [form, setForm] = useState(() => initial ? mergeInitial(initial) : blankForm());
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [confirmDup, setConfirmDup] = useState(false);
  const isEdit = !!initial;
  const health = livePreviewHealth(form);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleProduct = (slug) => setForm((f) => ({
    ...f,
    products_used: f.products_used.includes(slug)
      ? f.products_used.filter(p => p !== slug)
      : [...f.products_used, slug],
  }));

  const addGalleryUrl = (url) => {
    const u = (url || '').trim();
    if (!u) return;
    setForm((f) => ({ ...f, gallery_image_urls: [...f.gallery_image_urls, u] }));
  };
  const removeGalleryUrl = (i) => setForm((f) => ({
    ...f, gallery_image_urls: f.gallery_image_urls.filter((_, idx) => idx !== i),
  }));

  const checkDuplicates = async () => {
    if (isEdit) return [];
    try {
      const r = await axios.post(`${API}/admin/historic-events/check-duplicate`, {
        venue_name: form.venue_name, event_date: form.event_date,
        customer_name: form.customer_name,
      }, { headers: authHeaders, withCredentials: true });
      const dups = r.data?.duplicates || [];
      setDuplicates(dups);
      return dups;
    } catch { return []; }
  };

  const save = async (force = false) => {
    if (!form.title || !form.event_date || !form.venue_name) {
      toast.error('Title, event date and venue name are required');
      return;
    }
    if (!force && !isEdit) {
      const dups = await checkDuplicates();
      if (dups.length > 0) { setConfirmDup(true); return; }
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/admin/historic-events/${initial.slug}`,
          form, { headers: authHeaders, withCredentials: true });
        toast.success('Event updated ✦');
      } else {
        await axios.post(`${API}/admin/historic-events`,
          form, { headers: authHeaders, withCredentials: true });
        toast.success('Event saved · map pin live ✦');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="historic-events-form-modal" style={{
      position: 'fixed', inset: 0, background: 'rgba(8,5,3,0.78)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, zIndex: 1000, overflow: 'auto',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#1A120A', border: `1px solid ${GOLD_DIM}`,
        borderRadius: 14, padding: 28, maxWidth: 880, width: '100%',
        maxHeight: '92vh', overflow: 'auto',
        color: IVORY, fontFamily: SERIF,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
          <div>
            <div style={{ fontStyle: 'italic', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD }}>
              {isEdit ? '✦ Edit event' : '✦ New historic event'}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 500, margin: '6px 0 0', letterSpacing: '-0.015em' }}>
              {isEdit ? form.title || 'Edit event' : 'Add a past event'}
            </h2>
          </div>
          <div data-testid="form-health-pill" style={{
            background: 'rgba(0,0,0,0.42)', border: `1px solid ${health.color}`,
            color: health.color, padding: '6px 14px', borderRadius: 999,
            fontSize: 12, whiteSpace: 'nowrap',
          }}>Health {health.score}% · {health.label}</div>
        </div>

        {confirmDup && (
          <div style={{
            border: `1px solid ${RED}`, background: 'rgba(199,89,59,0.16)',
            color: IVORY, padding: 14, borderRadius: 12, marginBottom: 16,
          }} data-testid="duplicate-warning">
            <strong>Possible duplicates found ({duplicates.length}).</strong>
            <ul style={{ margin: '6px 0 10px 18px', fontSize: 13 }}>
              {duplicates.map(d => (
                <li key={d.slug}>
                  {d.title} · {d.venue_name} · {d.event_date}
                  {d.customer_match && ' · customer match'}
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => save(true)} data-testid="duplicate-save-anyway" style={pillBtnGhost}>Save Anyway</button>
              <button type="button" onClick={() => { setConfirmDup(false); setDuplicates([]); }} data-testid="duplicate-cancel" style={pillBtnGhost}>Cancel</button>
            </div>
          </div>
        )}

        {/* SECTION: Event basics */}
        <FormSection title="Event basics">
          <FieldGrid>
            <Field label="Event Title *">
              <input data-testid="form-title" type="text" value={form.title} onChange={(e) => update('title', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Event Date *">
              <input data-testid="form-event-date" type="date" value={form.event_date} onChange={(e) => update('event_date', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Event Type">
              <select data-testid="form-event-type" value={form.event_type} onChange={(e) => update('event_type', e.target.value)} style={inputStyle}>
                {Object.entries(EVENT_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Estimated Guest Count">
              <input data-testid="form-guest-count" type="number" min={0} value={form.estimated_guest_count}
                     onChange={(e) => update('estimated_guest_count', parseInt(e.target.value || '0', 10))} style={inputStyle} />
            </Field>
          </FieldGrid>
        </FormSection>

        {/* SECTION: Venue */}
        <FormSection title="Venue">
          <FieldGrid>
            <Field label="Venue Name *">
              <input data-testid="form-venue-name" type="text" value={form.venue_name} onChange={(e) => update('venue_name', e.target.value)} style={inputStyle} placeholder="e.g. Carden Park" />
            </Field>
            <Field label="Postcode (auto-geocoded)">
              <input data-testid="form-venue-postcode" type="text" value={form.venue_postcode} onChange={(e) => update('venue_postcode', e.target.value.toUpperCase())} style={inputStyle} placeholder="CH3 9DQ" />
            </Field>
            <Field label="Town / Location">
              <input data-testid="form-venue-location" type="text" value={form.venue_location} onChange={(e) => update('venue_location', e.target.value)} style={inputStyle} placeholder="Chester, Cheshire" />
            </Field>
            <Field label="Venue Contact (optional)">
              <input data-testid="form-venue-contact" type="text" value={form.venue_contact} onChange={(e) => update('venue_contact', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Venue Website (optional)">
              <input data-testid="form-venue-website" type="url" value={form.venue_website} onChange={(e) => update('venue_website', e.target.value)} style={inputStyle} placeholder="https://" />
            </Field>
          </FieldGrid>
        </FormSection>

        {/* SECTION: Products & Package */}
        <FormSection title="Products used (multi-select)">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} data-testid="form-products-used">
            {Object.entries(PRODUCT_LABELS).map(([slug, lbl]) => {
              const on = form.products_used.includes(slug);
              return (
                <button key={slug} type="button" onClick={() => toggleProduct(slug)}
                  data-testid={`form-product-${slug}`}
                  style={{
                    background: on ? GOLD : 'transparent',
                    color: on ? '#1A120A' : DIM,
                    border: `1px solid ${on ? GOLD : GOLD_DIM}`,
                    fontFamily: SERIF, fontSize: 12, padding: '7px 13px',
                    borderRadius: 999, cursor: 'pointer',
                  }}>{lbl}</button>
              );
            })}
          </div>
          <FieldGrid style={{ marginTop: 14 }}>
            <Field label="Package Slug">
              <input data-testid="form-package-slug" type="text" value={form.package_slug} onChange={(e) => update('package_slug', e.target.value)} style={inputStyle} placeholder="e.g. classic / premium / blossom-booth" />
            </Field>
          </FieldGrid>
        </FormSection>

        {/* SECTION: Customer Journey */}
        <FormSection title="Customer & booking journey">
          <FieldGrid>
            <Field label="Customer First Names">
              <input data-testid="form-customer-name" type="text" value={form.customer_name} onChange={(e) => update('customer_name', e.target.value)} style={inputStyle} placeholder="e.g. Lucy & James" />
            </Field>
            <Field label="Booking Source">
              <select data-testid="form-booking-source" value={form.booking_source} onChange={(e) => update('booking_source', e.target.value)} style={inputStyle}>
                <option value="">— Select —</option>
                {Object.entries(SOURCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </FieldGrid>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
            <Checkbox label="Customer left a review" testid="form-review-left" value={form.review_left} onChange={(v) => update('review_left', v)} />
            <Checkbox label="Gallery delivered" testid="form-gallery-delivered" value={form.gallery_delivered} onChange={(v) => update('gallery_delivered', v)} />
            <Checkbox label="Memory box supplied" testid="form-memory-box" value={form.memory_box_supplied} onChange={(v) => update('memory_box_supplied', v)} />
            <Checkbox label="Audio guestbook included" testid="form-audio-included" value={form.audio_guestbook_included} onChange={(v) => update('audio_guestbook_included', v)} />
          </div>
        </FormSection>

        {/* SECTION: Review */}
        <FormSection title="Review">
          <FieldGrid>
            <Field label="Rating (0-5)">
              <input data-testid="form-review-rating" type="number" min={0} max={5} step={0.5}
                     value={form.review_rating} onChange={(e) => update('review_rating', parseFloat(e.target.value || '0'))} style={inputStyle} />
            </Field>
          </FieldGrid>
          <Field label="Review text">
            <textarea data-testid="form-review-text" rows={3} value={form.review_text}
              onChange={(e) => update('review_text', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
        </FormSection>

        {/* SECTION: Assets — drag/drop or URL */}
        <FormSection title="Event assets (hero · gallery · video · drone · virtual tour)">
          <FieldGrid>
            <Field label="Hero Image URL">
              <input data-testid="form-hero-image-url" type="url" value={form.hero_image_url} onChange={(e) => update('hero_image_url', e.target.value)} style={inputStyle} placeholder="Paste a URL or upload below" />
            </Field>
            <Field label="Gallery Folder">
              <input data-testid="form-gallery-folder" type="text" value={form.gallery_folder} onChange={(e) => update('gallery_folder', e.target.value)} style={inputStyle} placeholder="e.g. carden-park-2024-08-22" />
            </Field>
            <Field label="Video URL (YouTube / Vimeo)">
              <input data-testid="form-video-url" type="url" value={form.video_url} onChange={(e) => update('video_url', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Drone Footage URL">
              <input data-testid="form-drone-url" type="url" value={form.drone_footage_url} onChange={(e) => update('drone_footage_url', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="360 / Virtual Tour URL">
              <input data-testid="form-virtual-tour-url" type="url" value={form.virtual_tour_url} onChange={(e) => update('virtual_tour_url', e.target.value)} style={inputStyle} />
            </Field>
          </FieldGrid>
          <GalleryUrlList urls={form.gallery_image_urls} onAdd={addGalleryUrl} onRemove={removeGalleryUrl} />
          <FileDropzone authHeaders={authHeaders} onUploaded={(url) => {
            if (!form.hero_image_url) {
              update('hero_image_url', url);
            } else {
              addGalleryUrl(url);
            }
          }} />
        </FormSection>

        {/* SECTION: Visibility & flags */}
        <FormSection title="Visibility & flags">
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Checkbox label="Public" testid="form-is-public" value={form.is_public} onChange={(v) => update('is_public', v)} />
            <Checkbox label="Featured event" testid="form-is-featured" value={form.is_featured} onChange={(v) => update('is_featured', v)} />
            <Checkbox label="Venue partner" testid="form-venue-partner" value={form.venue_partner} onChange={(v) => update('venue_partner', v)} />
          </div>
          <Field label="Notes (internal)">
            <textarea data-testid="form-notes" rows={2} value={form.notes}
              onChange={(e) => update('notes', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
        </FormSection>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button type="button" onClick={onClose} data-testid="form-cancel" style={pillBtnGhost}>Cancel</button>
          <button type="button" onClick={() => save(false)} disabled={saving} data-testid="form-save"
            style={{
              background: GOLD, color: '#1A120A', border: 'none',
              fontFamily: SERIF, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
              padding: '11px 22px', borderRadius: 999, cursor: saving ? 'wait' : 'pointer', fontWeight: 600,
              opacity: saving ? 0.7 : 1,
            }}
          >{saving ? 'Saving…' : (isEdit ? 'Save changes ✦' : 'Save & drop pin ✦')}</button>
        </div>
      </div>
    </div>
  );
};

const mergeInitial = (initial) => {
  const base = blankForm();
  Object.keys(base).forEach((k) => {
    if (initial[k] !== undefined && initial[k] !== null) base[k] = initial[k];
  });
  // legacy aliases
  base.title = base.title || initial.name || '';
  base.event_date = base.event_date || initial.date || '';
  base.hero_image_url = base.hero_image_url || initial.image_url || '';
  base.gallery_image_urls = base.gallery_image_urls?.length
    ? base.gallery_image_urls : (initial.gallery || []);
  return base;
};

const FormSection = ({ title, children }) => (
  <fieldset style={{
    border: `1px solid ${GOLD_DIM}`, borderRadius: 10, padding: '14px 16px',
    margin: '0 0 16px',
  }}>
    <legend style={{ padding: '0 10px', fontStyle: 'italic', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD }}>
      {title}
    </legend>
    {children}
  </fieldset>
);

const FieldGrid = ({ children, style }) => (
  <div style={{
    display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    ...style,
  }}>{children}</div>
);

const Field = ({ label, children }) => (
  <label style={{ display: 'block', fontSize: 12, color: DIM, letterSpacing: '0.04em', marginBottom: 8 }}>
    <div style={{ marginBottom: 5 }}>{label}</div>
    {children}
  </label>
);

const Checkbox = ({ label, value, onChange, testid }) => (
  <label data-testid={testid} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: IVORY, fontSize: 13, cursor: 'pointer' }}>
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  background: 'rgba(0,0,0,0.36)', border: `1px solid ${GOLD_DIM}`,
  color: IVORY, fontFamily: SERIF, fontSize: 14,
};

const GalleryUrlList = ({ urls, onAdd, onRemove }) => {
  const [draft, setDraft] = useState('');
  return (
    <div style={{ marginTop: 14 }} data-testid="form-gallery-urls">
      <div style={{ fontSize: 12, color: DIM, marginBottom: 6, fontStyle: 'italic' }}>
        Gallery image URLs ({urls.length})
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input data-testid="form-gallery-url-input" type="url" value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="https://…" style={{ ...inputStyle, flex: 1 }} />
        <button type="button" onClick={() => { onAdd(draft); setDraft(''); }} data-testid="form-gallery-url-add"
          style={pillBtnGhost}>+ Add URL</button>
      </div>
      {urls.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {urls.map((u, i) => (
            <div key={u + i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12, color: DIM }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{u}</span>
              <button type="button" onClick={() => onRemove(i)} data-testid={`gallery-url-remove-${i}`}
                style={{ ...pillBtnGhost, padding: '3px 9px', fontSize: 11 }}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FileDropzone = ({ authHeaders, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', f);
        try {
          // iter234j — use the dedicated generic admin upload endpoint.
          // The previous reuse of /admin/photobooth-media/event/general/photo
          // 404'd because that validator only accepts booth/package/addon
          // kinds. Returns {url, image_url, media_id}.
          // Axios infers the multipart boundary from FormData — don't
          // hard-set Content-Type or the boundary gets dropped.
          const r = await axios.post(
            `${API}/admin/uploads/image?folder=historic-events`, fd,
            { headers: authHeaders, withCredentials: true },
          );
          const url = r.data?.url || r.data?.image_url;
          if (url) onUploaded(url);
          else toast.error(`Upload returned no URL for ${f.name}`);
        } catch (err) {
          const detail = err?.response?.data?.detail || err.message || 'Unknown error';
          toast.error(`Upload failed for ${f.name}: ${detail}`);
        }
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="form-dropzone" style={{
      marginTop: 12, padding: 16, border: `1px dashed ${GOLD_DIM}`, borderRadius: 12,
      textAlign: 'center', color: DIM, fontSize: 12,
    }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}>
      {busy ? 'Uploading…' : 'Drop images here or'}
      <label style={{ marginLeft: 6, color: GOLD, cursor: 'pointer' }}>
        browse
        <input type="file" multiple accept="image/*" style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)} data-testid="form-dropzone-input" />
      </label>
    </div>
  );
};

// ─── iter231 Phase B · Bulk Toolbar ─────────────────────────────────
const BulkToolbar = ({ count, slugs, authHeaders, onCleared, onAfter }) => {
  const [busy, setBusy] = useState(false);
  const run = async (action, value) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/historic-events/bulk-action`, {
        slugs, action, value: value || null,
      }, { headers: authHeaders, withCredentials: true });
      toast.success(`✦ ${action.replace(/_/g, ' ')} · ${r.data?.affected ?? 0} events`);
      await onAfter();
    } catch {
      toast.error('Bulk action failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="bulk-toolbar" style={{
      position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)',
      background: '#1A120A', border: `1px solid ${GOLD}`, borderRadius: 999,
      padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 18px 50px rgba(0,0,0,0.55)', zIndex: 800,
      fontFamily: SERIF, fontSize: 13, color: IVORY, flexWrap: 'wrap', maxWidth: 'calc(100vw - 40px)',
    }}>
      <span style={{ color: GOLD, fontStyle: 'italic' }}>{count} selected</span>
      <button onClick={() => run('feature')} disabled={busy} style={pillBtnGhost} data-testid="bulk-feature">★ Feature</button>
      <button onClick={() => run('set_public')} disabled={busy} style={pillBtnGhost} data-testid="bulk-public">Make Public</button>
      <button onClick={() => run('set_private')} disabled={busy} style={pillBtnGhost} data-testid="bulk-private">Make Private</button>
      <button onClick={() => {
        const pkg = window.prompt('Assign package slug (e.g. blossom-booth):');
        if (pkg !== null) run('assign_package', { package_slug: pkg });
      }} disabled={busy} style={pillBtnGhost} data-testid="bulk-assign-package">Assign Package</button>
      <button onClick={() => run('regenerate_health')} disabled={busy} style={pillBtnGhost} data-testid="bulk-regen-health">↻ Health</button>
      <button onClick={() => run('regenerate_inspiration')} disabled={busy} style={pillBtnGhost} data-testid="bulk-regen-inspiration">↻ Inspiration</button>
      <button onClick={() => run('regenerate_seo')} disabled={busy} style={pillBtnGhost} data-testid="bulk-regen-seo">↻ SEO</button>
      <button onClick={async () => {
        if (window.confirm(`Delete ${count} events? This removes the map pins.`)) {
          await run('delete');
        }
      }} disabled={busy} style={{ ...pillBtnGhost, color: RED, borderColor: RED }} data-testid="bulk-delete">Delete</button>
      <button onClick={onCleared} disabled={busy} style={{ ...pillBtnGhost, color: DIM, borderColor: GOLD_DIM }} data-testid="bulk-clear">Clear</button>
    </div>
  );
};


// ─── iter231 Phase B · CSV Wizard ───────────────────────────────────
const ALL_FIELDS = [
  'title', 'event_date', 'event_type', 'estimated_guest_count',
  'venue_name', 'venue_postcode', 'venue_location', 'venue_contact', 'venue_website',
  'package_slug', 'products_used',
  'customer_name', 'booking_source',
  'review_text', 'review_rating',
  'hero_image_url', 'gallery_image_urls', 'gallery_folder',
  'video_url', 'drone_footage_url', 'virtual_tour_url',
  'is_public', 'is_featured', 'venue_partner',
];

const CsvWizardModal = ({ authHeaders, onClose, onImported }) => {
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dupRule, setDupRule] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);

  const onFileChosen = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(String(e.target.result || ''));
    reader.readAsText(file);
  };

  const runPreview = async () => {
    if (!csvText.trim()) {
      toast.error('Paste or upload a CSV first');
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/historic-events/csv/preview`,
        { csv_text: csvText },
        { headers: authHeaders, withCredentials: true });
      setPreview(r.data);
      setMapping(r.data.mapping || {});
      setStep(2);
    } catch (e) {
      toast.error('CSV preview failed');
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`${API}/admin/historic-events/csv/import`, {
        csv_text: csvText, mapping, duplicate_rule: dupRule,
      }, { headers: authHeaders, withCredentials: true });
      setReport(r.data?.report || null);
      setStep(5);
      toast.success(`✦ Imported ${r.data?.report?.events_imported || 0} events`);
    } catch (e) {
      toast.error('Import failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  const downloadReport = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.import_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="csv-wizard-modal" style={{
      position: 'fixed', inset: 0, background: 'rgba(8,5,3,0.78)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, zIndex: 1000, overflow: 'auto',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#1A120A', border: `1px solid ${GOLD_DIM}`,
        borderRadius: 14, padding: 28, maxWidth: 980, width: '100%',
        maxHeight: '92vh', overflow: 'auto', color: IVORY, fontFamily: SERIF,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
          <div>
            <div style={{ fontStyle: 'italic', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD }}>
              ✦ Bulk Import · Step {step} of 5
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 500, margin: '6px 0 0', letterSpacing: '-0.015em' }}>
              {step === 1 && 'Upload CSV'}
              {step === 2 && 'Column mapping'}
              {step === 3 && 'Preview rows'}
              {step === 4 && 'Import rules'}
              {step === 5 && 'Import complete ✦'}
            </h2>
          </div>
          <button type="button" onClick={onClose} data-testid="csv-wizard-close" style={pillBtnGhost}>✕ Close</button>
        </div>

        <WizardStepIndicator step={step} />

        {/* Step 1 — Upload */}
        {step === 1 && (
          <div data-testid="csv-step-1">
            <p style={{ color: DIM, fontStyle: 'italic', fontSize: 14, lineHeight: 1.6 }}>
              Drop a CSV with one row per event. Auto-detected columns include
              Date, Venue, Postcode, Package, Products, Customer, Rating, Review,
              Event Type, Guests. You can also paste it in manually.
            </p>
            <input
              type="file" accept=".csv,text/csv,text/plain"
              data-testid="csv-file-input"
              onChange={(e) => onFileChosen(e.target.files?.[0])}
              style={{ marginBottom: 14, color: DIM }}
            />
            <textarea
              data-testid="csv-text"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={10}
              placeholder="…or paste CSV here. First row = headers."
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
            />
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={runPreview} data-testid="csv-step-1-next"
                style={{ ...pillBtnGhost, background: GOLD, color: '#1A120A', borderColor: GOLD, fontWeight: 600 }}>
                Analyse CSV →
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Mapping */}
        {step === 2 && preview && (
          <div data-testid="csv-step-2">
            <p style={{ color: DIM, fontStyle: 'italic', fontSize: 13, marginBottom: 14 }}>
              Confirm how each CSV column maps to an EventDrop field. We
              guessed where we could — review and adjust.
            </p>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              {preview.headers.map(h => (
                <div key={h} style={{ background: CARD, border: `1px solid ${GOLD_DIM}`, borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 12, color: DIM, fontStyle: 'italic', marginBottom: 4 }}>{h}</div>
                  <select
                    data-testid={`csv-mapping-${h.replace(/[^a-zA-Z0-9]/g, '-')}`}
                    value={mapping[h] || ''}
                    onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                    style={inputStyle}>
                    <option value="">— ignore —</option>
                    {ALL_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(1)} style={pillBtnGhost}>← Back</button>
              <button onClick={() => setStep(3)} data-testid="csv-step-2-next"
                style={{ ...pillBtnGhost, background: GOLD, color: '#1A120A', borderColor: GOLD, fontWeight: 600 }}>
                Preview rows →
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Preview */}
        {step === 3 && preview && (
          <div data-testid="csv-step-3">
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', marginBottom: 16 }}>
              <StatTile label="Rows" value={preview.summary.rows_detected} testid="csv-stat-rows" />
              <StatTile label="Will Import" value={preview.summary.will_import} testid="csv-stat-import" />
              <StatTile label="Will Fail" value={preview.summary.will_fail} testid="csv-stat-fail" />
              <StatTile label="New Venues" value={preview.summary.new_venues} testid="csv-stat-new-venues" />
              <StatTile label="Existing" value={preview.summary.existing_venues} testid="csv-stat-existing-venues" />
              <StatTile label="Duplicates" value={preview.summary.duplicates_in_db} testid="csv-stat-duplicates" />
            </div>
            <div style={{ maxHeight: 360, overflow: 'auto', border: `1px solid ${GOLD_DIM}`, borderRadius: 10 }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }} data-testid="csv-preview-table">
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.42)', color: GOLD }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Venue</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Customer</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Package</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 200).map(r => {
                    const cls = r.classification?.status;
                    const color = cls === 'will_import' ? GREEN : cls === 'will_fail' ? RED : '#C9802E';
                    return (
                      <tr key={r.row_index} style={{ borderTop: `1px solid ${GOLD_DIM}`, color: DIM }} data-testid={`csv-row-${r.row_index}`}>
                        <td style={{ padding: '6px 10px' }}>{r.row_index + 1}</td>
                        <td style={{ padding: '6px 10px', color }}>{cls === 'will_import' ? '✓ ready' : cls === 'will_fail' ? '✕ ' + r.classification.reason : 'amber'}</td>
                        <td style={{ padding: '6px 10px' }}>{r.event.event_date || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{r.event.venue_name || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{r.event.customer_name || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{r.event.package_slug || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(2)} style={pillBtnGhost}>← Mapping</button>
              <button onClick={() => setStep(4)} data-testid="csv-step-3-next"
                style={{ ...pillBtnGhost, background: GOLD, color: '#1A120A', borderColor: GOLD, fontWeight: 600 }}>
                Rules →
              </button>
            </div>
          </div>
        )}

        {/* Step 4 — Rules */}
        {step === 4 && (
          <div data-testid="csv-step-4">
            <p style={{ color: DIM, fontStyle: 'italic', fontSize: 13, marginBottom: 14 }}>
              How should we handle duplicate events (same venue + same date as
              an existing record)?
            </p>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              {['skip', 'merge', 'replace'].map(r => (
                <label key={r} data-testid={`csv-rule-${r}`} style={{
                  background: dupRule === r ? 'rgba(212,170,82,0.16)' : CARD,
                  border: `1px solid ${dupRule === r ? GOLD : GOLD_DIM}`,
                  borderRadius: 10, padding: 14, cursor: 'pointer',
                }}>
                  <input type="radio" name="dup-rule" value={r}
                    checked={dupRule === r} onChange={() => setDupRule(r)} />
                  <strong style={{ marginLeft: 8, color: IVORY }}>
                    {r.toUpperCase()}
                  </strong>
                  <div style={{ marginTop: 6, fontSize: 12, color: DIM, fontStyle: 'italic' }}>
                    {r === 'skip' && 'Keep existing, ignore the new row'}
                    {r === 'merge' && 'Patch existing with non-empty new fields'}
                    {r === 'replace' && 'Delete existing and insert the new row'}
                  </div>
                </label>
              ))}
            </div>
            <p style={{ color: DIM, fontSize: 12, fontStyle: 'italic', marginTop: 18 }}>
              Imports automatically geocode postcodes, create venues if missing,
              compute Health Score and Inspiration Score, generate SEO tags
              and refresh map pins / venue intelligence / package stats.
            </p>
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(3)} style={pillBtnGhost}>← Preview</button>
              <button onClick={runImport} disabled={importing} data-testid="csv-step-4-import"
                style={{ ...pillBtnGhost, background: GOLD, color: '#1A120A', borderColor: GOLD, fontWeight: 600, opacity: importing ? 0.6 : 1 }}>
                {importing ? 'Importing…' : 'Run import ✦'}
              </button>
            </div>
          </div>
        )}

        {/* Step 5 — Report */}
        {step === 5 && report && (
          <div data-testid="csv-step-5">
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: 16 }}>
              <StatTile label="Imported" value={report.events_imported} testid="csv-report-imported" />
              <StatTile label="Merged" value={report.events_merged} testid="csv-report-merged" />
              <StatTile label="Replaced" value={report.events_replaced} testid="csv-report-replaced" />
              <StatTile label="Skipped" value={report.events_skipped} testid="csv-report-skipped" />
              <StatTile label="Venues +" value={report.venues_created} testid="csv-report-venues" />
              <StatTile label="Reviews" value={report.reviews_imported} testid="csv-report-reviews" />
              <StatTile label="Photos" value={report.photos_imported} testid="csv-report-photos" />
              <StatTile label="Errors" value={report.error_count} testid="csv-report-errors" />
              <StatTile label="Duration (s)" value={report.duration_seconds} testid="csv-report-duration" />
            </div>
            {report.errors?.length > 0 && (
              <details style={{ background: CARD, border: `1px solid ${RED}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                <summary style={{ color: RED, cursor: 'pointer' }}>{report.errors.length} error{report.errors.length === 1 ? '' : 's'}</summary>
                <ul style={{ marginTop: 8, fontSize: 12, color: DIM }}>
                  {report.errors.map((e, i) => (
                    <li key={i}>Row {e.row_index + 1}: {String(e.reason).slice(0, 200)}</li>
                  ))}
                </ul>
              </details>
            )}
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={downloadReport} data-testid="csv-report-download" style={pillBtnGhost}>↓ Download report</button>
              <button onClick={onImported} data-testid="csv-report-done"
                style={{ ...pillBtnGhost, background: GOLD, color: '#1A120A', borderColor: GOLD, fontWeight: 600 }}>
                Done ✦
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const WizardStepIndicator = ({ step }) => (
  <div style={{ display: 'flex', gap: 6, marginBottom: 22 }}>
    {[1, 2, 3, 4, 5].map(n => (
      <div key={n} style={{
        flex: 1, height: 4, borderRadius: 999,
        background: n <= step ? GOLD : 'rgba(212,170,82,0.18)',
      }} />
    ))}
  </div>
);


// ─── iter231 Phase B · Duplicates Centre ────────────────────────────
const DuplicatesModal = ({ authHeaders, onClose, onChanged }) => {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/historic-events/duplicates`,
        { headers: authHeaders, withCredentials: true });
      setClusters(r.data?.clusters || []);
    } catch { toast.error('Could not load duplicates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteEvent = async (slug) => {
    if (!window.confirm(`Delete event "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/historic-events/${slug}`,
        { headers: authHeaders, withCredentials: true });
      toast.success('Event deleted');
      await load();
      await onChanged();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="duplicates-modal" style={{
      position: 'fixed', inset: 0, background: 'rgba(8,5,3,0.78)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, zIndex: 1000, overflow: 'auto',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#1A120A', border: `1px solid ${GOLD_DIM}`, borderRadius: 14,
        padding: 28, maxWidth: 880, width: '100%',
        maxHeight: '92vh', overflow: 'auto', color: IVORY, fontFamily: SERIF,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
          <div>
            <div style={{ fontStyle: 'italic', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD }}>
              ✦ Duplicate Centre
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 500, margin: '6px 0 0' }}>
              {clusters.length === 0 ? 'No duplicates found' : `${clusters.length} cluster${clusters.length === 1 ? '' : 's'} found`}
            </h2>
          </div>
          <button type="button" onClick={onClose} style={pillBtnGhost} data-testid="duplicates-close">✕ Close</button>
        </div>
        {loading && <p style={{ color: DIM, fontStyle: 'italic' }}>Loading ✦</p>}
        {!loading && clusters.length === 0 && (
          <p style={{ color: DIM, fontStyle: 'italic' }}>
            Clean — no two events share the same venue + date.
          </p>
        )}
        {clusters.map((c) => (
          <div key={c.venue_slug + c.event_date} data-testid={`dup-cluster-${c.venue_slug}-${c.event_date}`}
            style={{
              background: CARD, border: `1px solid ${GOLD_DIM}`, borderRadius: 12,
              padding: 14, marginBottom: 10,
            }}>
            <div style={{ fontSize: 13, color: GOLD, fontStyle: 'italic', marginBottom: 8 }}>
              {c.venue_slug} · {c.event_date} · {c.count} events
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {c.events.map((ev) => (
                <div key={ev.slug} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <strong>{ev.title}</strong>
                    {ev.customer_name && <span style={{ color: DIM, fontStyle: 'italic' }}> · {ev.customer_name}</span>}
                    {ev.package_slug && <span style={{ color: DIM, fontStyle: 'italic' }}> · {ev.package_slug}</span>}
                  </div>
                  <button onClick={() => deleteEvent(ev.slug)}
                    data-testid={`dup-delete-${ev.slug}`}
                    style={{ ...pillBtnGhost, color: RED, borderColor: RED, fontSize: 11, padding: '5px 11px' }}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── iter231d · Promote Bookings to Map ─────────────────────────────
const PromoteBookingsModal = ({ authHeaders, onClose, onPromoted }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [hideOnlyUnpromoted, setHideOnlyUnpromoted] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/bookings-promotable`,
        { headers: authHeaders, withCredentials: true });
      setBookings(r.data?.bookings || []);
    } catch { toast.error('Could not load bookings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = bookings.filter(b => !hideOnlyUnpromoted || !b.promoted);
  const toggleAll = () => {
    if (selected.size === visible.filter(b => !b.promoted).length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.filter(b => !b.promoted).map(b => b.booking_id)));
    }
  };
  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const promote = async () => {
    if (selected.size === 0) { toast.error('Select bookings first'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/bookings/promote-to-map`, {
        booking_ids: Array.from(selected),
      }, { headers: authHeaders, withCredentials: true });
      const d = r.data || {};
      toast.success(`✦ Promoted ${d.promoted_count} · skipped ${d.skipped_count} · ${d.error_count} errors`);
      setSelected(new Set());
      await load();
      await onPromoted();
    } catch (err) {
      toast.error('Promotion failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="promote-bookings-modal" style={{
      position: 'fixed', inset: 0, background: 'rgba(8,5,3,0.78)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, zIndex: 1000, overflow: 'auto',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#1A120A', border: `1px solid ${GOLD_DIM}`, borderRadius: 14,
        padding: 28, maxWidth: 960, width: '100%',
        maxHeight: '92vh', overflow: 'auto', color: IVORY, fontFamily: SERIF,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={{ fontStyle: 'italic', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD }}>
              ✦ Promote Bookings to Map
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 500, margin: '6px 0 0', letterSpacing: '-0.015em' }}>
              Select bookings to pin onto the map
            </h2>
            <p style={{ fontSize: 13, color: DIM, fontStyle: 'italic', margin: '6px 0 0', lineHeight: 1.5 }}>
              Every booking with a venue + date can be promoted. Already-promoted
              bookings are dimmed. Each promotion drops a map pin, updates venue
              intelligence and product stats automatically.
            </p>
          </div>
          <button type="button" onClick={onClose} style={pillBtnGhost} data-testid="promote-bookings-close">✕ Close</button>
        </div>

        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: DIM, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={hideOnlyUnpromoted}
              onChange={(e) => setHideOnlyUnpromoted(e.target.checked)}
              data-testid="promote-hide-promoted" />
            Hide already-promoted
          </label>
          <button onClick={toggleAll} style={pillBtnGhost} data-testid="promote-toggle-all">
            {selected.size === visible.filter(b => !b.promoted).length && selected.size > 0 ? '☐ Deselect all' : '☑ Select all'}
          </button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: DIM }}>{selected.size} selected · {visible.length} visible</span>
          <button onClick={promote} disabled={busy || selected.size === 0}
            data-testid="promote-bookings-submit"
            style={{ ...pillBtnGhost, background: GOLD, color: '#1A120A',
                     borderColor: GOLD, fontWeight: 600,
                     opacity: (busy || selected.size === 0) ? 0.55 : 1 }}>
            {busy ? 'Promoting…' : 'Promote selected ✦'}
          </button>
        </div>

        {loading && <p style={{ color: DIM, fontStyle: 'italic' }}>Loading ✦</p>}
        {!loading && visible.length === 0 && (
          <div style={{ padding: 26, textAlign: 'center', background: CARD,
                        border: `1px dashed ${GOLD_DIM}`, borderRadius: 12 }}>
            <p style={{ color: DIM, fontStyle: 'italic' }} data-testid="promote-bookings-empty">
              {bookings.length === 0
                ? 'No bookings with a venue + date yet. Create one in Admin → Bookings first.'
                : 'Everything visible has already been promoted. Untick the filter to see them.'}
            </p>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map(b => {
            const dim = b.promoted;
            const checked = selected.has(b.booking_id);
            return (
              <label key={b.booking_id}
                data-testid={`promote-booking-row-${b.booking_id}`}
                style={{
                  background: CARD,
                  border: `1px solid ${checked ? GOLD : GOLD_DIM}`,
                  borderRadius: 10, padding: '10px 14px',
                  display: 'grid', gridTemplateColumns: 'auto 1fr auto',
                  gap: 12, alignItems: 'center', cursor: dim ? 'default' : 'pointer',
                  opacity: dim ? 0.55 : 1,
                }}
              >
                <input
                  type="checkbox" checked={checked} disabled={dim}
                  onChange={() => !dim && toggle(b.booking_id)}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`promote-booking-checkbox-${b.booking_id}`}
                  style={{ width: 18, height: 18 }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <strong style={{ color: IVORY, fontSize: 15 }}>{b.customer_name || '(no name)'}</strong>
                    {b.promoted && (
                      <span style={{ fontSize: 10, letterSpacing: '0.2em', color: GOLD, fontStyle: 'italic' }}>
                        ✓ ON MAP
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: DIM, fontStyle: 'italic', marginTop: 3 }}>
                    {[b.venue, b.event_date?.slice(0, 10), b.postcode, b.package_slug, b.status]
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span style={{ fontSize: 11, color: b.has_geo ? GREEN : RED, fontStyle: 'italic' }}>
                  {b.has_geo ? '✓ geo' : '✕ no geo'}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};


export default AdminHistoricEventsPage;
